import React, { useState } from 'react';
import ColorPicker from '../LoaderGenerator/ColorPicker';

const CommonModifiers = ({ modifiers = {}, onUpdate }) => {
  const [activeSection, setActiveSection] = useState('layout');

  const handleModifierChange = (path, value) => {
    const newModifiers = { ...modifiers };
    const keys = path.split('.');
    let current = newModifiers;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    onUpdate(newModifiers);
  };

  const getValue = (path, defaultValue = null) => {
    const keys = path.split('.');
    let current = modifiers;
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return defaultValue;
      }
    }
    // Treat null/undefined as "not set" so callers using defaults (e.g. colors)
    // don't accidentally pass null into components like react-color.
    return current === null || current === undefined ? defaultValue : current;
  };

  const SectionHeader = ({ id, title, children }) => (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
      <button
        onClick={() => setActiveSection(activeSection === id ? null : id)}
        className="flex items-center justify-between w-full text-left"
      >
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h4>
        <span className="text-gray-600 dark:text-gray-500">{activeSection === id ? '▼' : '▶'}</span>
      </button>
      {activeSection === id && <div className="mt-4">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Layout Modifiers */}
      <SectionHeader id="layout" title="Layout">
        {/* Padding */}
        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Padding</label>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">All Sides: {getValue('padding.all') || 0}px</label>
            <input
              type="range"
              min="0"
              max="50"
              value={getValue('padding.all') || 0}
              onChange={(e) => handleModifierChange('padding.all', parseInt(e.target.value) || null)}
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Horizontal: {getValue('padding.horizontal') || 0}px</label>
              <input
                type="range"
                min="0"
                max="50"
                value={getValue('padding.horizontal') || 0}
                onChange={(e) => handleModifierChange('padding.horizontal', parseInt(e.target.value) || null)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Vertical: {getValue('padding.vertical') || 0}px</label>
              <input
                type="range"
                min="0"
                max="50"
                value={getValue('padding.vertical') || 0}
                onChange={(e) => handleModifierChange('padding.vertical', parseInt(e.target.value) || null)}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Frame */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Frame</label>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Width: {getValue('frame.width') || 'auto'}px</label>
              <input
                type="range"
                min="0"
                max="500"
                value={getValue('frame.width') || 0}
                onChange={(e) => handleModifierChange('frame.width', parseInt(e.target.value) || null)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Height: {getValue('frame.height') || 'auto'}px</label>
              <input
                type="range"
                min="0"
                max="500"
                value={getValue('frame.height') || 0}
                onChange={(e) => handleModifierChange('frame.height', parseInt(e.target.value) || null)}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={getValue('frame.maxWidthInfinity', false)}
                onChange={(e) => handleModifierChange('frame.maxWidthInfinity', e.target.checked)}
                className="rounded"
              />
              Max Width: Infinity
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={getValue('frame.maxHeightInfinity', false)}
                onChange={(e) => handleModifierChange('frame.maxHeightInfinity', e.target.checked)}
                className="rounded"
              />
              Max Height: Infinity
            </label>
          </div>
        </div>
      </SectionHeader>

      {/* Appearance Modifiers */}
      <SectionHeader id="appearance" title="Appearance">
        {/* Background */}
        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Background</label>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {['none', 'color', 'gradient'].map((type) => (
              <button
                key={type}
                onClick={() => handleModifierChange('background.type', type)}
                className={`
                  px-3 py-2 rounded text-xs font-medium transition-all
                  ${
                    getValue('background.type', 'none') === type
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
          {getValue('background.type') === 'color' && (
            <ColorPicker
              color={getValue('background.color', '#FFFFFF')}
              onChange={(color) => handleModifierChange('background.color', color)}
              label="Background Color"
            />
          )}
          {getValue('background.type') === 'gradient' && (
            <div className="space-y-2">
              <ColorPicker
                color={getValue('background.gradientColors.0', '#F26F29')}
                onChange={(color) => {
                  const currentColors = getValue('background.gradientColors', ['#F26F29', '#F03224']);
                  const nextColors = Array.isArray(currentColors) ? [...currentColors] : ['#F26F29', '#F03224'];
                  nextColors[0] = color;
                  handleModifierChange('background.gradientColors', nextColors);
                }}
                label="Gradient Color 1"
              />
              <ColorPicker
                color={getValue('background.gradientColors.1', '#F03224')}
                onChange={(color) => {
                  const currentColors = getValue('background.gradientColors', ['#F26F29', '#F03224']);
                  const nextColors = Array.isArray(currentColors) ? [...currentColors] : ['#F26F29', '#F03224'];
                  nextColors[1] = color;
                  handleModifierChange('background.gradientColors', nextColors);
                }}
                label="Gradient Color 2"
              />
            </div>
          )}
        </div>

        {/* Foreground Color */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Foreground Color</label>
          <ColorPicker
            color={getValue('foregroundColor', '#000000')}
            onChange={(color) => handleModifierChange('foregroundColor', color)}
            label="Color"
          />
        </div>

        {/* Opacity */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Opacity: {getValue('opacity', 1.0).toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={getValue('opacity', 1.0)}
            onChange={(e) => handleModifierChange('opacity', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Corner Radius */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Corner Radius: {getValue('cornerRadius', 0)}px
          </label>
          <input
            type="range"
            min="0"
            max="50"
            value={getValue('cornerRadius', 0)}
            onChange={(e) => handleModifierChange('cornerRadius', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
      </SectionHeader>

      {/* Effects Modifiers */}
      <SectionHeader id="effects" title="Effects">
        {/* Shadow */}
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={getValue('shadow.enabled', false)}
              onChange={(e) => handleModifierChange('shadow.enabled', e.target.checked)}
              className="rounded"
            />
            Shadow
          </label>
          {getValue('shadow.enabled', false) && (
            <div className="space-y-2 pl-6">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Radius: {getValue('shadow.radius', 8)}px</label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={getValue('shadow.radius', 8)}
                  onChange={(e) => handleModifierChange('shadow.radius', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Opacity: {getValue('shadow.opacity', 0.2).toFixed(2)}</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={getValue('shadow.opacity', 0.2)}
                  onChange={(e) => handleModifierChange('shadow.opacity', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">X: {getValue('shadow.x', 0)}px</label>
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={getValue('shadow.x', 0)}
                    onChange={(e) => handleModifierChange('shadow.x', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Y: {getValue('shadow.y', 4)}px</label>
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={getValue('shadow.y', 4)}
                    onChange={(e) => handleModifierChange('shadow.y', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Border */}
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={getValue('border.enabled', false)}
              onChange={(e) => handleModifierChange('border.enabled', e.target.checked)}
              className="rounded"
            />
            Border
          </label>
          {getValue('border.enabled', false) && (
            <div className="space-y-2 pl-6">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Width: {getValue('border.width', 1)}px</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={getValue('border.width', 1)}
                  onChange={(e) => handleModifierChange('border.width', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              <ColorPicker
                color={getValue('border.color', '#CCCCCC')}
                onChange={(color) => handleModifierChange('border.color', color)}
                label="Border Color"
              />
            </div>
          )}
        </div>

        {/* Rotation */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rotation: {getValue('rotation', 0)}°
          </label>
          <input
            type="range"
            min="-360"
            max="360"
            value={getValue('rotation', 0)}
            onChange={(e) => handleModifierChange('rotation', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Scale */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Scale</label>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">X: {getValue('scale.x', 1.0).toFixed(2)}</label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={getValue('scale.x', 1.0)}
                onChange={(e) => handleModifierChange('scale.x', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Y: {getValue('scale.y', 1.0).toFixed(2)}</label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={getValue('scale.y', 1.0)}
                onChange={(e) => handleModifierChange('scale.y', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Offset */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Offset</label>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">X: {getValue('offset.x', 0)}px</label>
              <input
                type="range"
                min="-100"
                max="100"
                value={getValue('offset.x', 0)}
                onChange={(e) => handleModifierChange('offset.x', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-gray-600 dark:text-gray-400 mb-1">Y: {getValue('offset.y', 0)}px</label>
              <input
                type="range"
                min="-100"
                max="100"
                value={getValue('offset.y', 0)}
                onChange={(e) => handleModifierChange('offset.y', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      {/* Shape Modifiers */}
      <SectionHeader id="shape" title="Shape">
        {/* Clip Shape */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Clip Shape</label>
          <div className="grid grid-cols-2 gap-2">
            {['none', 'circle', 'roundedRectangle', 'capsule', 'ellipse'].map((shape) => (
              <button
                key={shape}
                onClick={() => handleModifierChange('clipShape', shape)}
                className={`
                  px-3 py-2 rounded text-xs font-medium transition-all
                  ${
                    getValue('clipShape', 'none') === shape
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {shape === 'roundedRectangle' ? 'Rounded Rect' : shape.charAt(0).toUpperCase() + shape.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={getValue('aspectRatio.enabled', false)}
              onChange={(e) => handleModifierChange('aspectRatio.enabled', e.target.checked)}
              className="rounded"
            />
            Aspect Ratio
          </label>
          {getValue('aspectRatio.enabled', false) && (
            <div className="space-y-2 pl-6">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Ratio: {getValue('aspectRatio.ratio', 1.0).toFixed(2)}:1</label>
                <input
                  type="range"
                  min="0.1"
                  max="3"
                  step="0.1"
                  value={getValue('aspectRatio.ratio', 1.0)}
                  onChange={(e) => handleModifierChange('aspectRatio.ratio', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Content Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  {['fit', 'fill'].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => handleModifierChange('aspectRatio.contentMode', mode)}
                      className={`
                        px-3 py-2 rounded text-xs font-medium transition-all
                        ${
                          getValue('aspectRatio.contentMode', 'fit') === mode
                            ? 'bg-gradient-primary text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                        }
                      `}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionHeader>
    </div>
  );
};

export default CommonModifiers;
